import type { EcsConfigStateSdkSnapshot, EcsConfigStateSnapshot } from 'owa-analytics-types';
import { trace } from 'owa-trace';

let nextSdkId = 0;
let revision = 0;
const localSnapshots = new Map<number, EcsConfigStateSnapshot>();
const remoteSnapshots = new Map<object, readonly EcsConfigStateSdkSnapshot[]>();
const listeners = new Set<() => void>();
const localListeners = new Set<() => void>();
const endedWorkerOwners = new WeakSet<object>();
const workerLifetimes = new WeakMap<
    object,
    { owners: Set<object>; cleanups: Set<() => void>; ended: boolean }
>();
const ownerLifetimes = new WeakMap<
    object,
    {
        lifetime: { owners: Set<object>; cleanups: Set<() => void>; ended: boolean };
        cleanup?: () => void;
    }
>();

export function bindEcsConfigStateWorkerLifetime(
    worker: Pick<Worker, 'terminate'>,
    owner: object,
    onTerminate?: () => void
): void {
    let lifetime = workerLifetimes.get(worker);
    if (!lifetime) {
        lifetime = { owners: new Set(), cleanups: new Set(), ended: false };
        workerLifetimes.set(worker, lifetime);
        const current = lifetime;
        const terminate = worker.terminate;
        // Only this tracked worker instance is wrapped; errors and reporter
        // replacement do not imply that its SDKs have stopped stamping.
        worker.terminate = function (this: Pick<Worker, 'terminate'>) {
            terminate.call(this);
            if (this !== worker || current.ended) {
                return;
            }
            current.ended = true;
            for (const source of current.owners) {
                endedWorkerOwners.add(source);
                remoteSnapshots.delete(source);
                ownerLifetimes.delete(source);
            }
            current.owners.clear();
            notifyListeners(current.cleanups);
            current.cleanups.clear();
            notifyChanges();
        };
    }
    if (lifetime.ended) {
        endedWorkerOwners.add(owner);
    } else {
        lifetime.owners.add(owner);
        ownerLifetimes.set(owner, { lifetime, cleanup: onTerminate });
        if (onTerminate) {
            lifetime.cleanups.add(onTerminate);
        }
    }
}

export function endEcsConfigStateWorkerOwner(owner: object): void {
    endEcsConfigStateWorkerOwnerInternal(owner, true);
}

function endEcsConfigStateWorkerOwnerInternal(owner: object, notify: boolean): void {
    if (endedWorkerOwners.has(owner)) {
        return;
    }
    endedWorkerOwners.add(owner);
    remoteSnapshots.delete(owner);
    const record = ownerLifetimes.get(owner);
    if (record) {
        record.lifetime.owners.delete(owner);
        if (record.cleanup) {
            record.lifetime.cleanups.delete(record.cleanup);
        }
        ownerLifetimes.delete(owner);
    }
    if (notify) {
        notifyChanges();
    }
}

export function retainEcsConfigStateSdkSnapshot(mapping: EcsConfigStateSnapshot): () => void {
    const id = ++nextSdkId;
    localSnapshots.set(id, { ...mapping });
    notifyLocalChanges();
    return () => {
        if (localSnapshots.delete(id)) {
            notifyLocalChanges();
        }
    };
}

export function getLocalEcsConfigStateSdkSnapshots(): EcsConfigStateSdkSnapshot[] {
    return Array.from(localSnapshots, ([id, mapping]) => ({ id, mapping }));
}

export function getLocalEcsConfigStateSdkSnapshotRevision(): number {
    return revision;
}

export function getRetainedEcsConfigStateSdkMappings(): EcsConfigStateSnapshot[] {
    return [
        ...localSnapshots.values(),
        ...Array.from(remoteSnapshots.values()).flatMap(snapshots =>
            snapshots.map(snapshot => snapshot.mapping)
        ),
    ];
}

export function setRemoteEcsConfigStateSdkSnapshots(
    owner: object,
    snapshots: readonly EcsConfigStateSdkSnapshot[]
): void {
    if (endedWorkerOwners.has(owner)) {
        return;
    }
    if (snapshots.length === 0) {
        remoteSnapshots.delete(owner);
    } else {
        remoteSnapshots.set(
            owner,
            snapshots.map(snapshot => ({
                id: snapshot.id,
                mapping: { ...snapshot.mapping },
            }))
        );
    }
    notifyChanges();
}

export function subscribeEcsConfigStateSdkSnapshots(
    listener: () => void,
    localOnly = false
): () => void {
    const target = localOnly ? localListeners : listeners;
    target.add(listener);
    return () => {
        target.delete(listener);
    };
}

function notifyLocalChanges(): void {
    revision++;
    notifyListeners(localListeners);
    notifyChanges();
}

function notifyChanges(): void {
    notifyListeners(listeners);
}

function notifyListeners(subscribers: ReadonlySet<() => void>): void {
    for (const listener of subscribers) {
        try {
            listener();
        } catch {
            trace.warn('Config-state SDK snapshot listener failed.', 'analytics');
        }
    }
}
